import * as React from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { IconButton } from '@coursera/cds-core';
import { AIGenerateBrandedNewIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import StoreProviderWrapper from 'bundles/ai-coach-platform/components/patterns/chat/store/StoreProviderWrapper';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import SparkleOpenIcon from 'bundles/unified-common/components/coach-on-discovery/SparkleOpenIcon';
import { CONVERSATIONAL_DISCOVERY_COACH_CONFIG } from 'bundles/unified-common/constants/globalSparkleNavConstants';
import { useGlobalCoachChatState } from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';
import { useNavAiSparkle } from 'bundles/unified-common/providers/NavAiSparkleExperimentProvider';

import _t from 'i18n!nls/composable-page-header';

const NavAiSparkleGridItemContent = () => {
  const tooltip = _t('Ask Coursera');
  const track = useTracker();
  const { coachOpen, setCoachOpen, contextConfig } = useGlobalCoachChatState();
  const { isMessageLoading, isMessageStreaming } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
    isMessageStreaming: state.behavior.isMessageStreaming,
  }));

  const disableButton = !coachOpen && (isMessageLoading || isMessageStreaming);

  const trackCoachOpen = useCallback<() => void>(() => {
    track('interact_coach', {
      coachInteractionType: 'click_action',
      coach: {
        mode: CONVERSATIONAL_DISCOVERY_COACH_CONFIG.coachModeId,
        location: 'page_header',
      },
      coachAction: CONVERSATIONAL_DISCOVERY_COACH_CONFIG.coachActionId,
    });
  }, [track]);

  const trackCoachClose = useCallback<() => void>(() => {
    track('interact_coach', {
      coachInteractionType: 'close_coach_tool',
      coach: {
        mode: contextConfig.config.coachModeId,
        location: 'page_header',
      },
      coachAction: contextConfig.config.coachActionId,
    });
  }, [track, contextConfig.config.coachModeId, contextConfig.config.coachActionId]);

  const onButtonClick = useCallback<() => void>(() => {
    if (coachOpen) {
      trackCoachClose();
    } else {
      trackCoachOpen();
    }
    setCoachOpen((prev) => !prev);
  }, [coachOpen, trackCoachClose, trackCoachOpen, setCoachOpen]);

  return (
    <IconButton
      aria-label={tooltip}
      tooltip={tooltip}
      onClick={onButtonClick}
      data-testid="coursera-ai-sparkle"
      disabled={disableButton}
      variant="ghost"
      icon={coachOpen ? <SparkleOpenIcon size="large" /> : <AIGenerateBrandedNewIcon size="large" />}
    />
  );
};

const NavAiSparkleGridItem = () => {
  const { showNavAiSparkle } = useNavAiSparkle();
  if (!showNavAiSparkle) return null;

  return (
    <StoreProviderWrapper>
      <NavAiSparkleGridItemContent />
    </StoreProviderWrapper>
  );
};

export default NavAiSparkleGridItem;
